import * as Sentry from "@sentry/react";
import * as ReactDOM from "react-dom/client";
import App from "./app";
import config from "./config";
import "./styles/index.css";
import { initDiagnosticRingBuffer } from "./components/defect-reporter/diagnostic-ring-buffer";
import { AnalyticsEnvironmentProvider } from "./components/layouts/providers/analytics-provider";
import { PostHogEnvironmentProvider } from "./components/layouts/providers/posthog-provider";

import { isAbortError, isOfflineNetworkError } from "./utils/error-handling";

initDiagnosticRingBuffer();

/**
 * Initialize Sentry as early as possible in your application's lifecycle.
 */
if (config.sentry.dsn) {
	Sentry.init({
		dsn: config.sentry.dsn,
		environment: config.sentry.environment,
		integrations: [
			Sentry.browserTracingIntegration(),
			Sentry.replayIntegration(),
		],
		// Tracing
		tracesSampleRate: 1.0, //  Capture 100% of the transactions
		// Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
		tracePropagationTargets: [
			"localhost",
			/^\//,
			/^https:\/\/backend-proto\.illumi\.systems\/graphql/,
		],
		// Session Replay
		replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
		replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
		ignoreErrors: [
			"AbortError",
			"The operation was aborted",
			"The user aborted a request.",
			"ResizeObserver loop completed with undelivered notifications.",
			"ResizeObserver loop limit exceeded",
		],
		beforeSend(event, hint) {
			const error = hint?.originalException;
			if (error && (isAbortError(error) || isOfflineNetworkError(error))) {
				return null;
			}
			return event;
		},
	});
}

// biome-ignore lint/style/noNonNullAssertion: can assume that the element exists
const element = document.getElementById("root")!;

ReactDOM.createRoot(element).render(
	<PostHogEnvironmentProvider>
		<AnalyticsEnvironmentProvider>
			<App />
		</AnalyticsEnvironmentProvider>
	</PostHogEnvironmentProvider>,
);
